import { ChangeDetectionStrategy, Component, inject, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Event, NavigationStart, Router } from '@angular/router';
import { MarketplaceStoreFacade } from '@core/store/services/marketplace.store.facade';
import { TProductEntity } from '@core/utils/products/entities/product.entity';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { areUrlPartsChanged } from '@shared/cart/utils/are-url-parts-changed';
import { EnMaxWidthScreen } from '@shared/constants/screen-breakpoints';
import { distinctUntilChanged, filter, map, Observable } from 'rxjs';

import { CartImports } from './cart.imports';
import { CartAnalyticsService } from './services/cart.analytics.service';
import { CartViewService } from './services/cart.view.service';
import { CartViewFlags } from './states/cart.view.flags';

@UntilDestroy()
@Component({
  selector: 'noda-cart',
  standalone: true,
  imports: CartImports,
  providers: [CartAnalyticsService],
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartComponent implements OnInit {
  protected readonly enMaxWidthScreen: typeof EnMaxWidthScreen = EnMaxWidthScreen;
  private viewService: CartViewService = inject(CartViewService);
  public viewFlags: CartViewFlags = this.viewService.getFullState('flags');
  private analyticsService: CartAnalyticsService = inject(CartAnalyticsService);
  private storeFacade: MarketplaceStoreFacade = inject(MarketplaceStoreFacade);
  private router: Router = inject(Router);
  private counter$: Observable<number> = this.storeFacade.selectedProducts$.pipe(
    map((products: TProductEntity[]): number => products?.length ?? 0),
    untilDestroyed(this),
  );
  protected counter: Signal<number> = toSignal(this.counter$, {
    initialValue: 0,
  });

  ngOnInit(): void {
    this.subscribeToCounter();
    this.subscribeToRouterEvents();
  }

  public toggleCart(): void {
    const cartState: boolean = this.viewFlags.isCartOpen();

    this.viewService.setItemState(!cartState);
    this.analyticsService.trackCartToggle(!cartState);
  }

  public onStartClick(): void {
    this.analyticsService.trackStartButtonClick();
  }

  public onProductToggle([
    product,
    valueToToggle,
  ]: [
    TProductEntity,
    boolean,
  ]): void {
    this.storeFacade.toggleProduct(product);
    this.analyticsService.trackProductToggle(product, valueToToggle);
  }

  private subscribeToCounter(): void {
    this.counter$.pipe(distinctUntilChanged()).subscribe((): void => {
      if (this.viewFlags.isCartOpen() && this.counter() === 0) {
        this.viewService.setItemState(false);
      }
    });
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(
        filter(
          (event: Event): boolean =>
            this.viewFlags.isCartOpen() &&
            Boolean(event instanceof NavigationStart) &&
            !areUrlPartsChanged(this.router.url, (event as NavigationStart).url),
        ),
      )
      .subscribe(() => {
        this.viewService.setItemState(false);
      });
  }
}
